/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject } from 'rxjs';

import { ModalCloseActionType } from 'app/modules/social/modal-create-social/modal-create-social.types';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import DEFAULT_SOCIAL_SETTINGS from 'app/modules/social/social-permission/constants';
import { socialHomePageActions } from 'app/modules/social/state/home-page';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';
import SocialSettings from 'entity/SocialSettings';
import EntryOptionType from 'enums/EntryOptionType';
import { SocialPermissionType } from 'enums/SocialPermissionType';
import SocialController from 'rest/SocialController';

import { ScootaversePageService } from './scootaverse-page.service';

@Injectable({
  providedIn: 'root',
})
export class ScootaverseEditService extends Loggable implements OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  public isLoading = false;

  public constructor(
    private store: Store,
    private socialPageService: SocialPageService,
    private scootaversePageService: ScootaversePageService,
    private _toasterService: ToastrService,
    private router: Router,
  ) {
    super();
    this.setDisplayName('ScootaversePageService');
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async getRoomSettings(socialId: string): Promise<SocialSettings> {
    const socialSettings = await new SocialController().getSocialSettings(socialId);
    if (Object.keys(socialSettings.settings ?? {}).length !== 0) {
      return socialSettings;
    }
    return { settings: DEFAULT_SOCIAL_SETTINGS };
  }

  public async saveRoom(toSaveData?: {
    action: ModalCloseActionType;
    room: Social;
    socialSettings: any;
  }): Promise<void> {
    this.isLoading = true;

    if (!toSaveData) {
      await this.router.navigate(['']);
      this.isLoading = false;
      return;
    }
    const { action, room: newRoom, socialSettings } = toSaveData;
    if (!newRoom) {
      this.error('onEditRoom: editing complete, but room is undefined');
      this.isLoading = false;
      return;
    }

    const { socialId } = newRoom;
    assertDefined(socialId);

    const settings: SocialSettings = {
      settings: socialSettings?.settings,
      setSettings: socialSettings?.setSettings,
    };
    this.store.dispatch(socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({ settings: settings, roomId: socialId }));

    this.store.dispatch(socialHomePageActions.EDIT_ROOM_REQ({ room: newRoom }));

    if (action === ModalCloseActionType.SETTINGS) {
      await this.scootaversePageService.onAdvanceSettingsRoom(newRoom, true);
    } else if (action === ModalCloseActionType.START) {
      await this.socialPageService.onEnterRoom(newRoom);
    }
    this._toasterService.success('Room settings saved successfully!', 'Success');
    this.isLoading = false;
  }

  public async createScootaverse(): Promise<SocialRoomSet | undefined> {
    const toCreateScootaverse: SocialRoomSet = {
      name: 'Untitled Scootaverse',
      socialPermissionType: SocialPermissionType.PUBLIC,
      entryOption: EntryOptionType.DEFAULT,
      allowGuestUsers: true,
      anyoneCanStart: true,
    };

    const scootaverseCreated = await new SocialController().createEmptySocialRoomSet(toCreateScootaverse);
    if (scootaverseCreated) {
      this._toasterService.success('Scootaverse created successfully!', 'Success');
      await this.scootaversePageService.onCreateScootaverse(scootaverseCreated);
      return scootaverseCreated;
    }
    this._toasterService.error('Error creating scootaverse. Try again later.', 'Error');
    return undefined;
  }
}
